import * as XLSX from "xlsx";
import toast from "react-hot-toast";

const formatKeys = (arr) => arr.map(obj => {
  const newObj = {};
  for (const key in obj) {
    if (!key) continue;
    const newKey = String(key).charAt(0).toUpperCase() + String(key).slice(1).toLowerCase();
    newObj[newKey] = obj[key];
  }
  return newObj;
});

/**
 * Exports an array of inventory items to Excel.
 * @param {Array} inventory 
 */
export const downloadInventoryAsExcel = (inventory) => {
  if (!inventory || inventory.length === 0) {
    toast.error("No data to export");
    return;
  }

  try {
    const data = inventory.map(record => {
      const item = record.item || {};

      // Category
      const cat = item.category || record.category;
      const catLabel = typeof cat === 'object' && cat !== null
        ? (cat.categoryName || cat.title || cat.name)
        : (cat || '');

      // Unit
      const unitObj = item.units || record.units;
      const unitLabel = typeof unitObj === 'object' && unitObj !== null
        ? (unitObj.unitName || unitObj.title || unitObj.name)
        : (unitObj || record.unit || 'nos');

      // Brand
      const brandObj = item.brand || record.brand;
      const brandLabel = typeof brandObj === 'object' && brandObj !== null
        ? (brandObj.brandName || brandObj.title || brandObj.name)
        : (brandObj || '');

      // Vendor
      const vendorObj = record.vendor;
      const vendorLabel = typeof vendorObj === 'object' && vendorObj !== null
        ? (vendorObj.vendorName || vendorObj.name || vendorObj.title)
        : (vendorObj || '');

      // Stock status
      const avail = Number(record.availableQty || 0);
      const minQ  = Number(record.minQty || 0);
      const status = avail <= 0 ? 'Out of Stock' : (minQ > 0 && avail <= minQ ? 'Low Stock' : 'In Stock');

      // Last purchase date
      const lastDate = record.createdAt ? new Date(record.createdAt).toLocaleDateString('en-IN') : '';

      return {
        'Item Name':          item.itemName || item.name || record.name || "",
        'Item Code':          item.itemCode || record.itemCode || "",
        'Category':           String(catLabel),
        'Brand':              String(brandLabel),
        'Unit of Measure':    String(unitLabel),
        'Vendor / Supplier':  String(vendorLabel),
        'Invoice Number':     record.invoiceNo || record.invoiceNumber || item.invoiceNo || item.invoiceNumber || "",
        'Total Stock':        Number(record._stock || 0),
        'Available Quantity': avail,
        'Min Stock Level':    minQ,
        'Total Cost (₹)':    Number(record.totalCost || 0),
        'Status':             status,
        'Last Purchase Date': lastDate,
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);

    // Column widths
    const wscols = Object.keys(data[0]).map(k => ({ wch: Math.max(k.length + 2, 18) }));
    ws['!cols'] = wscols;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inventory Master");
    XLSX.writeFile(wb, `Generic_Inventory_${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.xlsx`);
    toast.success("Inventory exported!");
  } catch (error) {
    console.error("Export Error:", error);
    toast.error("Failed to export inventory");
  }
};


const resolveName = (value, list) => {
  if (!value) return '';
  if (typeof value === 'object') return value.storeName || value.vendorName || value.title || value.name || value.label || value.categoryName || '—';
  
  const idStr = String(value);
  if (/^[0-9a-fA-F]{24}$/.test(idStr) && list) {
    const match = list.find(item => String(item._id) === idStr);
    if (match) return match.name || match.title || match.storeName || match.vendorName || match.label;
  }
  return idStr;
};

/**
 * Exports an array of purchase records to Excel.
 * @param {Array} purchases 
 * @param {Object} dynamicSettings
 */
export const downloadPurchasesAsExcel = (purchases, dynamicSettings = {}) => {
  if (!purchases || purchases.length === 0) {
    toast.error("No data to export");
    return;
  }

  const { suppliers = [], stores = [] } = dynamicSettings;

  try {
    const data = purchases.flatMap(p => {
      const vendorName = resolveName(p.vendor || p.supplier, suppliers);
      const storeName = resolveName(p.store, stores);
      
      const invoiceNo = p.invoiceNo || p.invoiceNumber || "";
      const invoiceDate = p.purchaseDate ? new Date(p.purchaseDate).toLocaleDateString() : (p.date || p.createdAt ? new Date(p.date || p.createdAt).toLocaleDateString() : "");
      const billNo = p.billNo || "";
      const paymentMode = p.paymentMode || p.payment?.paymentMode || "";

      const isTransaction = p.items && Array.isArray(p.items);
      const itemsToMap = isTransaction ? p.items : [p];

      if (itemsToMap.length === 0) {
        return [{
          'Invoice Number': invoiceNo,
          'Purchase Date': invoiceDate,
          'Vendor': vendorName,
          'Store': storeName,
          'Item Name': '—',
          'Qty': 0,
          'Rate': 0,
          'Discount %': 0,
          'Tax %': 0,
          'Tax Amount': 0,
          'Item Total Amount': 0,
          'Invoice Net Bill': p.finalNetPayment || p.netPayment || p.totalAmount || p.netBill || 0,
          'Notes': p.notes || p.description || ""
        }];
      }

      return itemsToMap.map(it => {
        const itm = it.item || it;
        const itemName = itm.itemName || itm.name || it.itemName || "Unknown Item";
        
        const itQty = Number(it.quantity || it.qty || it.originalQuantity || it.initialQty || 0);
        const itRate = Number(it.purchaseRate || it.rate || it.unitPrice || it.price || 0);
        
        const discPer = Number(it.discountPercent || it.discountP || 0);
        const taxPer = Number(it.taxPercent || it.taxP || 0);
        
        const baseAmount = itQty * itRate;
        const discountAmt = (baseAmount * discPer) / 100;
        const taxable = baseAmount - discountAmt;
        
        const itTax = Number(it.taxAmount) || (taxable * taxPer / 100);
        const itTotal = Number(it.totalAmount) || (taxable + itTax);

        return {
          'Invoice Number': invoiceNo,
          'Purchase Date': invoiceDate,
          'Vendor': vendorName,
          'Store': storeName,
          'Item Name': itemName,
          'Qty': itQty,
          'Rate': itRate,
          'Discount %': discPer,
          'Tax %': taxPer,
          'Tax Amount': itTax,
          'Item Total Amount': itTotal,
          'Invoice Net Bill': p.finalNetPayment || p.netPayment || p.totalAmount || p.netBill || 0,
          'Notes': p.notes || p.description || ""
        };
      });
    });

    const formattedData = formatKeys(data);
    const ws = XLSX.utils.json_to_sheet(formattedData);
    
    // Set column widths
    const headers = Object.keys(formattedData[0]);
    const wscols = headers.map(() => ({ wch: 20 }));
    ws['!cols'] = wscols;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Purchases");
    XLSX.writeFile(wb, `Generic_Purchases_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`);
    toast.success("Purchases exported!");
  } catch (error) {
    console.error("Export Error:", error);
    toast.error("Failed to export purchases");
  }
};

/**
 * Exports an array of sales/dispatches to Excel.
 * @param {Array} sales 
 */
export const downloadSalesAsExcel = (sales) => {
  if (!sales || sales.length === 0) {
    toast.error("No data to export");
    return;
  }

  try {
    const data = sales.flatMap(s => {
      const isMaterialRequest = s.recordType === 'material_request';
      const itemsArray = s.items || s.originalRequest?.items || [];
      const itemsToMap = itemsArray.length > 0 ? itemsArray : [s];
      
      const invoiceNo = s.invoiceNo || s.requestNo || s._id?.toString().slice(-6).toUpperCase() || "";
      const date = s.date || s.createdAt ? new Date(s.date || s.createdAt).toLocaleDateString() : "";
      const customer = s.customer || s.customerName || "";
      const phone = s.customerPhone || "";
      const recordType = isMaterialRequest ? 'Material Dispatch' : 'Retail Sale';

      const billingAddress = [s.billingAddressLine1, s.billingAddressLine2, s.billingCity, s.billingState, s.billingPincode].filter(Boolean).join(", ");
      const deliveryAddress = [s.deliveryAddressLine1, s.deliveryAddressLine2, s.deliveryCity, s.deliveryState, s.deliveryPincode].filter(Boolean).join(", ");
      
      const remarks = s.remarks || s.notes || "";
      const extraCharges = (s.charges || []).reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
      const adjustment = Number(s.adjustment || 0);
      const netPayable = Number(s.netPayable || s.netPayment || s.totalAmount || s.netBill || 0);
      
      if (itemsArray.length === 0) {
         return [{
            'Invoice / Ref No': invoiceNo,
            'Date': date,
            'Customer Name': customer,
            'Customer Phone': phone,
            'Record Type': recordType,
            'Billing Address': billingAddress,
            'Shipping Address': deliveryAddress,
            'Item Name': '—',
            'Qty': 0,
            'Rate': 0,
            'Discount %': 0,
            'Tax %': 0,
            'Tax Amount': 0,
            'Item Total': 0,
            'Other Charges': extraCharges,
            'Adjustment': adjustment,
            'Net Payable': netPayable,
            'Remarks': remarks
         }];
      }

      return itemsToMap.map(it => {
         const itemData = it.item || {};
         const name = itemData.itemName || itemData.name || it.itemName || it.name || "Item";
         const qty = Number(it.qty || it.deliveredQty || it.quantity || 0);
         const rate = Number(it.rate || it.unitPrice || itemData.rate || itemData.unitPrice || itemData.price || itemData.purchasePrice || 0);
         const discountPercent = Number(it.discountPercent || 0);
         const taxPercent = Number(it.taxPercent || 0);
         const taxAmount = Number(it.taxAmount || 0);
         const itemTotal = Number(it.amount || it.totalAmount || (qty * rate));
         
         return {
            'Invoice / Ref No': invoiceNo,
            'Date': date,
            'Customer Name': customer,
            'Customer Phone': phone,
            'Record Type': recordType,
            'Billing Address': billingAddress,
            'Shipping Address': deliveryAddress,
            'Item Name': name,
            'Qty': qty,
            'Rate': rate,
            'Discount %': discountPercent,
            'Tax %': taxPercent,
            'Tax Amount': taxAmount,
            'Item Total': itemTotal,
            'Other Charges': extraCharges,
            'Adjustment': adjustment,
            'Net Payable': netPayable,
            'Remarks': remarks
         };
      });
    });

    const formattedData = formatKeys(data);
    const ws = XLSX.utils.json_to_sheet(formattedData);
    
    // Set column widths
    const headers = Object.keys(formattedData[0]);
    const wscols = headers.map(() => ({ wch: 20 }));
    ws['!cols'] = wscols;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sales & Dispatches");
    XLSX.writeFile(wb, `Generic_Sales_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`);
    toast.success("Sales exported!");
  } catch (error) {
    console.error("Export Error:", error);
    toast.error("Failed to export sales");
  }
};

/**
 * Exports an array of return records to Excel.
 * @param {Array} returns 
 */
export const downloadReturnsAsExcel = (returns, isDamaged = false) => {
  if (!returns || returns.length === 0) {
    toast.error("No data to export");
    return;
  }

  try {
    const data = returns.map(r => {
      if (isDamaged) {
        // Calculate damaged items summary from multiple sources
        const damageReport = r.originalData?.damageReport || [];
        const originalDamagedItems = (r.originalData?.items || []).filter(item => (item.damagedQty && item.damagedQty > 0) || (item.damageReport && item.damageReport.length > 0));
        const currentDamagedItems = r.rawType === 'damage' 
            ? (r.items || []).map(it => ({ ...it, damagedQty: it.qty || it.quantity || 0 }))
            : (r.items || []).filter(item => (item.damagedQty && item.damagedQty > 0) || (item.damageReport && item.damageReport.length > 0));
        
        let parsedDamagedItems = [];
        const notes = r.originalData?.notes || r.notes || '';
        if (notes.includes('Damage Report:') && damageReport.length === 0 && originalDamagedItems.length === 0 && currentDamagedItems.length === 0) {
            const damageMatch = notes.match(/Damage Report:\s*(\d+)\s*item\(s\)\s*damaged/i);
            if (damageMatch) {
                const damagedCount = parseInt(damageMatch[1]);
                let damageReason = 'Damage reported during return processing';
                if (notes.includes('broken')) damageReason = 'Items broken during handling';
                else if (notes.includes('scratched')) damageReason = 'Items scratched during handling';
                else if (notes.includes('defective')) damageReason = 'Items found defective';
                else if (notes.includes('damaged')) {
                    const beforeDamageReport = notes.split('Damage Report:')[0].trim();
                    if (beforeDamageReport && beforeDamageReport.length > 0) {
                        damageReason = beforeDamageReport;
                    }
                }
                const returnItems = r.originalData?.items || r.items || [];
                const actualItem = returnItems[0];
                parsedDamagedItems = [{
                    damagedQty: damagedCount,
                    reason: damageReason,
                    item: { 
                        name: actualItem?.item?.name || actualItem?.itemName || actualItem?.name || 'Unknown Item',
                        _id: actualItem?.item?._id || actualItem?._id
                    }
                }];
            }
        }
        
        const damagedItems = damageReport.length > 0 ? damageReport : [...originalDamagedItems, ...currentDamagedItems, ...parsedDamagedItems];
        
        const enrichedDamagedItems = damagedItems.map(item => {
            let itemReason = item.reason;
            if (!itemReason) {
                if (notes.includes('broken')) itemReason = 'Items broken during handling';
                else if (notes.includes('scratched')) itemReason = 'Items scratched during handling';
                else if (notes.includes('defective')) itemReason = 'Items found defective';
                else if (notes.includes('damaged')) {
                    const beforeDamageReport = notes.split('Damage Report:')[0].trim();
                    if (beforeDamageReport && beforeDamageReport.length > 0) {
                        itemReason = beforeDamageReport;
                    }
                }
            }
            
            const originalItems = r.originalData?.items || r.items || [];
            let originalItem = null;
            originalItem = originalItems.find(oi => {
                const oiInvId = oi.inventoryId?._id || oi.inventoryId;
                const oiItemId = oi.item?._id || oi.item;
                const targetId = item.item?._id || item.item || item.inventoryId?._id || item.inventoryId;
                if (!targetId) return false;
                return (
                    (oiInvId && oiInvId.toString() === targetId.toString()) ||
                    (oiItemId && oiItemId.toString() === targetId.toString()) ||
                    (oi._id && oi._id.toString() === targetId.toString())
                );
            });
            if (!originalItem && originalItems.length > 0) {
                originalItem = originalItems[0];
            }
            const itemRate = originalItem 
                ? (originalItem.rate || originalItem.inventoryId?.purchaseRate || originalItem.inventoryId?.unitPrice || originalItem.unitPrice || 0)
                : 0;
            const name = originalItem 
                ? (originalItem.itemName || originalItem.item?.name || originalItem.item?.itemName || originalItem.inventoryId?.item?.itemName || originalItem.inventoryId?.item?.name || originalItem.name || 'Unknown Item')
                : (item.item?.name || item.itemName || item.name || 'Unknown Item');
            
            return { ...item, name, reason: itemReason || 'Damage reported', rate: itemRate };
        });
        
        const totalDamagedQty = enrichedDamagedItems.reduce((sum, item) => {
            let qty = Number(item.damagedQty) || 0;
            if (qty === 0 && item.damageReport && item.damageReport.length > 0) {
                qty = item.damageReport.reduce((acc, rep) => acc + (Number(rep.qty || rep.quantity) || 0), 0);
            }
            return sum + qty;
        }, 0);
        
        const totalDamagedAmount = enrichedDamagedItems.reduce((sum, item) => {
            let qty = Number(item.damagedQty) || 0;
            if (qty === 0 && item.damageReport && item.damageReport.length > 0) {
                qty = item.damageReport.reduce((acc, rep) => acc + (Number(rep.qty || rep.quantity) || 0), 0);
            }
            return sum + (qty * (Number(item.rate) || 0));
        }, 0);

        const itemsDetail = enrichedDamagedItems.map(it => {
            const damagedQtyToPrint = (it.damagedQty && it.damagedQty > 0) ? it.damagedQty : (it.damageReport ? it.damageReport.reduce((acc, rep) => acc + (Number(rep.qty || rep.quantity) || 0), 0) : 0);
            return `${it.name} (Damaged Qty: ${damagedQtyToPrint}, Rate: ${it.rate || 0}, Reason: ${it.reason})`;
        }).join(" | ");

        return {
          'Return ID': r.returnId || r.returnNo || r._id || "",
          'Return Type': r.returnType || "",
          'Date': r.date ? new Date(r.date).toLocaleDateString() : "",
          'Party (Customer/Vendor)': r.party || "",
          'Project ID / Ref': r.projectDisplayId || r.projectId || r.invoiceNo || "",
          'Total Damaged Qty': totalDamagedQty,
          'Damaged Item Details': itemsDetail || "No items",
          'Damaged Value': Number(totalDamagedAmount),
          'Reason / Notes': r.reason || r.notes || r.remarks || "",
          'Status': r.status || ""
        };
      }

      // Default (not damaged)
      const itemsDetail = (r.items || []).map(it => {
        const name = it.itemName || it.item?.itemName || it.item?.name || it.name || "Item";
        const qty = it.quantity || it.qty || 0;
        const rate = it.inventoryId?.purchaseRate || it.inventoryId?.unitPrice || it.unitPrice || it.rate || 0;
        return `${name} (Qty: ${qty}, Rate: ${rate})`;
      }).join(" | ");

      return {
        'Return ID': r.returnId || r.returnNo || r._id || "",
        'Return Type': r.returnType || "",
        'Date': r.date ? new Date(r.date).toLocaleDateString() : "",
        'Party (Customer/Vendor)': r.party || "",
        'Project ID / Ref': r.projectDisplayId || r.projectId || r.invoiceNo || "",
        'Total Items': r.itemQuantity || 0,
        'Item Details': itemsDetail || r.itemSummary || "No items",
        'Total Amount': Number(r.amount || r.totalAmount || r.totalValue || 0),
        'Reason / Notes': r.reason || r.notes || r.remarks || "",
        'Status': r.status || ""
      };
    });

    const formattedData = formatKeys(data);
    const ws = XLSX.utils.json_to_sheet(formattedData);
    
    // Set column widths
    const headers = Object.keys(formattedData[0]);
    const wscols = headers.map(() => ({ wch: 20 }));
    ws['!cols'] = wscols;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isDamaged ? "Damaged Returns" : "Returns");
    XLSX.writeFile(wb, `Generic_${isDamaged ? 'Damaged_Returns' : 'Returns'}_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`);
    toast.success(`${isDamaged ? 'Damaged returns' : 'Returns'} exported!`);
  } catch (error) {
    console.error("Export Error:", error);
    toast.error("Failed to export returns");
  }
};

/**
 * Exports an array of material requests to Excel.
 * @param {Array} requests 
 */
export const downloadMaterialRequestsAsExcel = (requests) => {
  if (!requests || requests.length === 0) {
    toast.error("No data to export");
    return;
  }

  try {
    const data = requests.map(r => {
      const proj = r.projectDetails || r.project || {};
      const customerName = proj.customerName || "";
      const customerId = proj.leadContact?.customId || proj.customerCustomId || "";
      
      const itemsDetail = (r.items || []).map(it => {
        const itemData = it.item || {};
        const name = itemData.itemName || itemData.name || it.itemName || "Item";
        const requested = Number(it.quantity || it.qty || it.requiredQty || 0);
        const delivered = Number(it.deliveredQty !== undefined ? it.deliveredQty : (it.deliveredQuantity || 0));
        return `${name} (Req: ${requested}, Del: ${delivered})`;
      }).join(" | ");

      const totalReq = (r.items || []).reduce((sum, it) => sum + Number(it.quantity || it.qty || it.requiredQty || 0), 0);
      const totalDel = (r.items || []).reduce((sum, it) => sum + Number(it.deliveredQty !== undefined ? it.deliveredQty : (it.deliveredQuantity || 0)), 0);

      return {
        'Request ID': r._id ? r._id.toString().slice(-6).toUpperCase() : "",
        'Date': r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "",
        'Project / Customer': customerName,
        'Customer ID': customerId,
        'Requested By': r.requestedBy?.name || (r.requestedBy?.firstName ? `${r.requestedBy.firstName} ${r.requestedBy.lastName || ''}`.trim() : null) || (typeof r.requestedBy === 'string' ? r.requestedBy : "Unknown"),
        'Total Items Requested': totalReq,
        'Total Items Delivered': totalDel,
        'Item Details': itemsDetail || "No items",
        'Status': r.status ? r.status.toUpperCase() : ""
      };
    });

    const formattedData = formatKeys(data);
    const ws = XLSX.utils.json_to_sheet(formattedData);
    
    // Set column widths
    const headers = Object.keys(formattedData[0]);
    const wscols = headers.map(() => ({ wch: 20 }));
    ws['!cols'] = wscols;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Material Requests");
    XLSX.writeFile(wb, `Generic_Requests_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`);
    toast.success("Requests exported!");
  } catch (error) {
    console.error("Export Error:", error);
    toast.error("Failed to export requests");
  }
};
